import { inject, Injectable } from '@angular/core';
import { RealtimeActiveService } from '@ddv/behaviors';
import { UserService } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import { LayoutService, ManagerService } from '@ddv/layout';
import {
    Client,
    DashboardFilter,
    DashboardPreference,
    QueryPeriodTypeName,
    QueryTypeName,
    UserPreferences,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { ClientsService } from '@ddv/reference-data';
import { deepClone } from '@ddv/utils';
import { removeTradeReleaseComment, WidgetConfigurationManager } from '@ddv/widgets';
import { BehaviorSubject, map, Observable, ReplaySubject, switchMap, Subject } from 'rxjs';

import { WidgetOnBoardService } from './widget-on-board.service';
import { DatePickerInfo } from '../models/date-picker-info';

@Injectable()
export class DdvWidgetService {
    private readonly layoutService = inject(LayoutService);
    private readonly manager = inject(ManagerService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly userService = inject(UserService);
    private readonly clientsService = inject(ClientsService);
    private readonly namedQueryService = inject(NamedQueriesService);
    private readonly realtimeActiveService = inject(RealtimeActiveService);
    private readonly widgetOnBoardService = inject(WidgetOnBoardService);

    readonly detailWidgetId: Observable<number | undefined>;
    readonly currentOpenedDetailWidgetId: Observable<number | undefined>;
    readonly datePickerInfo: Observable<DatePickerInfo>;
    userPreferences: UserPreferences | undefined;

    private dashboardQueryParams: DashboardPreference | undefined;
    private viewLevelClients: Client[] | undefined;
    private fullClientList: Client[] | undefined;
    private originalViewDates: { startDate: string, endDate: string, activeDate: string } | undefined;
    private originalViewFilters: DashboardFilter[] | undefined;
    private detailWidgetsOpened: number[] = [];
    private readonly detailWidgetIdSubject: Subject<number | undefined> = new ReplaySubject(1);
    private readonly currentOpenedDetailWidgetIdSubject: Subject<number | undefined> = new BehaviorSubject<number | undefined>(undefined);
    private readonly datePickerInfoSubject: Subject<DatePickerInfo> = new ReplaySubject<DatePickerInfo>(1);

    constructor() {
        this.detailWidgetId = this.detailWidgetIdSubject.asObservable();
        this.currentOpenedDetailWidgetId = this.currentOpenedDetailWidgetIdSubject.asObservable();
        this.datePickerInfo = this.datePickerInfoSubject.asObservable();

        this.queryParamsService.dashboardQueryParams
            .subscribe({
                next: (params) => this.dashboardQueryParams = params,
            });

        this.userService.userPreferences$
            .subscribe({
                next: (userPreferences) => this.userPreferences = userPreferences,
            });

        this.clientsService.clients()
            .subscribe({
                next: (clients) => {
                    this.fullClientList = clients.map((client) => {
                        return { clientId: client, clientName: client };
                    });
                },
            });
    }

    openDetailWidgetInView(
        widgetId: number,
        widgetClientCode: string,
        clientCode: string,
        costCenter?: string,
    ): Observable<void> {
        this.realtimeActiveService.pauseRealtimeUpdates();

        return this.widgetOnBoardService.addDetailWidgetToBoard(clientCode, widgetId)
            .pipe(
                switchMap((widgetState) => {
                    removeTradeReleaseComment(widgetState, costCenter);

                    return this.namedQueryService.fetchNamedQuery(widgetState.namedQueryId).pipe(
                        map((namedQuery) => {
                            const isNetSettlementBreaksQuery = namedQuery?.normalizeQueryTypeName() === QueryTypeName.SETTLEMENT_BREAKS;
                            return { widgetState, isNetSettlementBreaksQuery };
                        }),
                    );
                }),
                // eslint-disable-next-line complexity
                map(({ widgetState, isNetSettlementBreaksQuery }) => {
                    if (widgetClientCode && widgetClientCode !== clientCode) {
                        if (widgetState.displayNameType === 'CUSTOM') {
                            widgetState.customDisplayName = `${widgetClientCode} - ${widgetState.customDisplayName}`;
                        } else {
                            widgetState.name = `${widgetClientCode} - ${widgetState.name}`;
                        }
                    }
                    widgetState.isDetailWidget = true;
                    const widget = WidgetConfigurationManager.getWidgetConfigForWorkspace(
                        widgetState,
                        this.layoutService.getWidgetDefaultPosition(widgetState.widgetPosition));
                    widget.styleClasses = widgetState.enableWidgetHeader ? '' : 'hide-header';
                    widget.isDetailWidget = true;
                    this.manager.createWidget(widget);
                    this.manager.updateIsDetailWidgetOpened(true);
                    this.updateCurrentOpenedDetailWidgetId(widgetId);

                    const periodType = widgetState.namedQuery?.periodType;
                    const isCalendarVisible = !periodType || periodType !== QueryPeriodTypeName.NO_CALENDAR;
                    const isDateRangeSupported = !periodType ||
                        periodType === QueryPeriodTypeName.RANGE_WITH_ACTIVE_DATE ||
                        periodType === QueryPeriodTypeName.RANGE;
                    const isActiveDateSupported = !periodType || periodType === QueryPeriodTypeName.RANGE_WITH_ACTIVE_DATE;
                    this.updateDatePickerInfo(isCalendarVisible, isDateRangeSupported, isActiveDateSupported);

                    this.originalViewDates = {
                        startDate: this.dashboardQueryParams?.startDate ?? '',
                        endDate: this.dashboardQueryParams?.endDate ?? '',
                        activeDate: this.dashboardQueryParams?.activeDate ?? '',
                    };
                    this.originalViewFilters = this.dashboardQueryParams?.filters;
                    const startDate = widgetState.widgetFilters?.startDate;
                    const endDate = widgetState.widgetFilters?.endDate;
                    const activeDate = widgetState.widgetFilters?.activeDate;
                    const queryParams: DashboardPreference = {
                        startDate,
                        endDate,
                        activeDate,
                        filters: [],
                    };

                    if (widgetClientCode) {
                        const userPreferencesClients: Client[] = this.userPreferences?.clients?.map((client) => {
                            return { clientId: client.clientCode, clientName: client.clientCode };
                        }) ?? [];

                        this.viewLevelClients = deepClone(
                            this.dashboardQueryParams?.clients?.length ?
                                this.dashboardQueryParams.clients :
                                userPreferencesClients?.length ?
                                    userPreferencesClients :
                                    this.fullClientList);

                        queryParams.clients = [{ clientId: widgetClientCode, clientName: widgetClientCode }];
                    }

                    if (isNetSettlementBreaksQuery) {
                        queryParams.includeManuallyReleased = false;
                    }

                    this.queryParamsService.dispatchUpdatedQueryParams(queryParams);
                    this.manager.getWorkspace()?.extraParameters?.widgets.push(widgetState);
                    this.detailWidgetsOpened.push(widgetState.id ?? 0);
                }));
    }

    restoreOriginalViewFilters(): void {
        this.viewLevelClients = undefined;
        this.originalViewFilters = this.dashboardQueryParams?.filters;
    }

    resetDetailWidgetAndViewQueryParams(): void {
        this.detailWidgetsOpened = [];
        this.viewLevelClients = undefined;
        this.originalViewDates = undefined;
        this.originalViewFilters = undefined;
        this.manager.updateIsDetailWidgetOpened(false);
        this.updateDetailWidgetId(undefined);
    }

    removeLastOpenedDetailWidgetFromList(): void {
        this.realtimeActiveService.resumeRealtimeUpdates();

        this.updateDetailWidgetId(undefined);
        this.detailWidgetsOpened.pop();
        const detailWidgetsCount = this.detailWidgetsOpened.length;
        const currentDetailWidgetId = detailWidgetsCount ? this.detailWidgetsOpened[detailWidgetsCount - 1] : undefined;
        this.updateCurrentOpenedDetailWidgetId(currentDetailWidgetId);
    }

    setPreviousViewLevelQueryParams(): void {
        const filters: DashboardPreference = { acknowledged: false };
        if (this.viewLevelClients) {
            filters.clients = this.viewLevelClients;
            this.viewLevelClients = undefined;
        }

        if (this.originalViewDates) {
            const { startDate, endDate, activeDate } = this.originalViewDates;
            filters.startDate = startDate;
            filters.endDate = endDate;
            filters.activeDate = activeDate;
            this.originalViewDates = undefined;
        }

        if (this.originalViewFilters) {
            filters.filters = this.originalViewFilters;
            this.originalViewFilters = undefined;
        }

        this.queryParamsService.dispatchUpdatedQueryParams(filters);
    }

    isDetailWidgetLastOpened(widgetId: number): boolean {
        return this.detailWidgetsOpened[this.detailWidgetsOpened.length - 1] === widgetId;
    }

    // called when:
    //   - a WidgetOnBoard component is initialized and the widget on it is a detail widget
    //   - removeLastOpenedDetailWidgetFromList, resetDetailWidgetAndViewQueryParams to clear it out
    updateDetailWidgetId(widgetId: number | undefined): void {
        this.detailWidgetIdSubject.next(widgetId);
    }

    // called when:
    //   - openDetailWidgetInView to set the currently opened detail widget, but after the named query is loaded
    //   - removeLastOpenedDetailWidgetFromList to clear it out
    private updateCurrentOpenedDetailWidgetId(widgetId: number | undefined): void {
        this.currentOpenedDetailWidgetIdSubject.next(widgetId);
    }

    private updateDatePickerInfo(isCalendarVisible: boolean, isDateRangeSupported: boolean, isActiveDateSupported: boolean): void {
        this.datePickerInfoSubject.next({ isCalendarVisible, isDateRangeSupported, isActiveDateSupported });
    }
}
